import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, forwardRef } from "react";
import DatePicker from "react-datepicker";
import { motion, AnimatePresence } from "framer-motion";
import api from "../api"; // ✅ centralized API instance
import "react-datepicker/dist/react-datepicker.css";
import "../css/ShopDetails.css";
import PawAnimation from "./PawAnimation";

import Sidebar from "./Sidebar";
import Header from "./Header";
import PageTitle from "./PageTitleCompoent";
import FilterIcon2 from "../assets/Icons/FilterIcon2";
import { FaCalendar, FaStar } from "react-icons/fa";
import { FiPhone } from "react-icons/fi";
import { MdOutlineMail } from "react-icons/md";
import { IoLocationOutline, IoEyeOutline } from "react-icons/io5";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";

const ShopDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // ✅ get shop ID from URL param
  const shopId = id;

  const handleBack = () => navigate(-1);

  const [showFilter, setShowFilter] = useState(false);
  const [startDate, setStartDate] = useState(new Date("2025-04-23"));
  const [endDate, setEndDate] = useState(new Date("2025-05-23"));

  const [shopDetails, setShopDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const toggleFilter = () => setShowFilter((prev) => !prev);

  // ✅ Fetch shop details dynamically via shopId
  useEffect(() => {
    if (!shopId) {
      setError("No shop selected.");
      return;
    }

    const fetchShopDetails = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get("/admin/shop-orders/", {
          params: { shop_id: shopId },
        });

        if (res.data && res.data.shop) {
          setShopDetails(res.data);
        } else {
          setError("No shop details found.");
        }
      } catch (err) {
        console.error("Failed to fetch shop details:", err);
        setError("Failed to fetch shop details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchShopDetails();
  }, [shopId]);

  // === Dynamic Sales Summary ===
  const salesSummary = shopDetails
    ? [
        {
          label: "Total Sales",
          value: `₹${shopDetails.total_cost.toLocaleString()}`,
        },
        { label: "Total Products", value: shopDetails.total_products },
        {
          label: "Overall Revenue",
          value: `₹${shopDetails.overall_revenue.toLocaleString()}`,
        },
        {
          label: "Top-Selling Product",
          value: shopDetails.top_selling_product?.product?.name || "N/A",
        },
        {
          label: "Pawfriends Revenue",
          value: `₹${shopDetails.pawfriends_revenue.toLocaleString()}`,
        },
      ]
    : [];

  // === Dynamic Product Data ===
  const productData = shopDetails
    ? shopDetails.orders.map((order) => {
        const p = order.product;
        return {
          name: p.name,
          category: p.product_category,
          stock: p.quantity,
          price: parseFloat(p.actual_price),
          status:
            p.quantity > 20
              ? "Available"
              : p.quantity > 0
              ? "Low Stock"
              : "Not Available",
        };
      })
    : [];

  const getStatusClass = (status) => {
    if (status === "Available") return "status-available";
    if (status === "Low Stock") return "status-low";
    return "status-unavailable";
  };

  // ✅ Custom Date Input
  const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <div className="custom-date-input" onClick={onClick} ref={ref}>
      <span className="selected-date">{value || "Select date"}</span>
      <FaCalendar className="calendar-icon" />
    </div>
  ));

  const applyFilter = () => {
    console.log("Filter applied:", startDate, endDate);
  };

  // ✅ Handle View Document
  const handleViewDocument = () => {
    if (shopDetails?.shop?.document) {
      window.open(shopDetails.shop.document, "_blank");
    } else {
      alert("No document uploaded for this shop.");
    }
  };

  if (loading) {
    return (
      <div className="full-screen-loader">
        <PawAnimation />
      </div>
    );
  }

  return (
    <div className="container">
      <Sidebar />
      <div className="main">
        <Header />
        <div className="shop-detail-container">
          <PageTitle title="Shop Details" onBack={handleBack} />
          {error && <p className="error">{error}</p>}

          {!loading && !error && shopDetails && (
            <motion.div
              className="shop-detail-main-container"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Shop Summary */}
              <motion.div
                className="shop-summary"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="shop-info-card">
                  <div className="card-header">
                    {shopDetails.shop.shop_name}
                  </div>
                  <div className="shop-card-body">
                    <img
                      src={
                        shopDetails.shop.logo ||
                        "https://via.placeholder.com/80"
                      }
                      alt="shop logo"
                      className="shop-logo"
                    />
                    <div className="shop-details">
                      <h2>{shopDetails.shop.shop_name}</h2>
                      <p className="gst">
                        GSTN: {shopDetails.shop.gstin_number}
                      </p>
                      <p>Owner: {shopDetails.shop.owner_name}</p>
                      <div className="shop_details_icons">
                        <FiPhone /> {shopDetails.shop.contact_number}
                      </div>
                    </div>
                  </div>
                  <div className="shop-location-details">
                    <div className="shop_details_icons">
                      <MdOutlineMail /> {shopDetails.shop_owner?.email || "N/A"}
                    </div>
                    <div className="shop_details_icons">
                      <IoLocationOutline /> {shopDetails.shop.address}
                    </div>
                    <div className="shop_details_icons">
                      <FaStar className="star_icons" /> 5.0{" "}
                      <span>(100 reviews)</span>
                    </div>
                    <div className="shop_details_icons documents">
                      <div>Uploaded Documents</div>
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        className="view-btn"
                        onClick={handleViewDocument}
                      >
                        <IoEyeOutline className="view-btn-icon" /> View
                      </motion.div>
                    </div>
                  </div>
                </div>

                {/* Sales Summary */}
                <div className="sales-summary-card">
                  <div className="sales-card-header">
                    <div>Sales Summary</div>
                    <div className="filter-container">
                      <div
                        className="sales-card-filterIcon"
                        onClick={toggleFilter}
                      >
                        <FilterIcon2 />
                      </div>

                      <AnimatePresence>
                        {showFilter && (
                          <motion.div
                            className="date-filter-popup"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="date-filter-inputs">
                              <label className="date-filter-label">From:</label>
                              <DatePicker
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                dateFormat="dd-MM-yyyy"
                                customInput={<CustomDateInput />}
                              />
                              <label className="date-filter-label">To:</label>
                              <DatePicker
                                selected={endDate}
                                onChange={(date) => setEndDate(date)}
                                dateFormat="dd-MM-yyyy"
                                customInput={<CustomDateInput />}
                              />
                            </div>
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              className="filter-apply-btn"
                              onClick={applyFilter}
                            >
                              <IoMdCheckmarkCircleOutline className="filter-apply-btn-icon" />{" "}
                              Apply
                            </motion.div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                  <div className="sales-summary">
                    <div className="sales-summary-items">
                      {salesSummary.map((item, index) => (
                        <p key={index}>
                          {item.label} <span>{item.value}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Product Overview */}
              <motion.div
                className="product-overview"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h3>Product Overview</h3>
                <table className="product-overview-table">
                  <thead className="product-overview-table-header">
                    <tr className="product-overview-table-row">
                      <th>Product Name</th>
                      <th>Category</th>
                      <th>Stock</th>
                      <th>Price</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody className="product-overview-table-body">
                    {productData.map((product, index) => (
                      <motion.tr
                        key={index}
                        className="product-overview-table-row"
                        whileHover={{
                          backgroundColor: "#f9f9f9",
                          scale: 1.01,
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        <td className="product-overview-data">{product.name}</td>
                        <td className="product-overview-data">
                          {product.category}
                        </td>
                        <td className="product-overview-data">
                          {product.stock}
                        </td>
                        <td className="product-overview-data">
                          ₹{product.price.toLocaleString()}
                        </td>
                        <td className={getStatusClass(product.status)}>
                          {product.status}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopDetails;
